import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import api from "@/lib/api";

const HEARTBEAT_MS = 20000;

const sinceLabel = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  return `${mins} min${mins === 1 ? "" : "s"} ago`;
};

/**
 * Tells an author when a colleague already has this article open. Heartbeats
 * every 20s while the editor is mounted and releases the claim on unmount, so
 * the warning disappears as soon as the other tab closes.
 */
export function KbEditingPresence({ slug, onOthersChange }) {
  const [others, setOthers] = useState([]);

  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;
    const beat = async () => {
      try {
        const { data } = await api.post(`/kb/articles/${slug}/editing/heartbeat`);
        if (alive) setOthers(data.others || []);
      } catch { /* presence is best-effort — never block editing */ }
    };
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => {
      alive = false;
      clearInterval(timer);
      api.post(`/kb/articles/${slug}/editing/release`).catch(() => {});
    };
  }, [slug]);

  useEffect(() => { onOthersChange?.(others); }, [others, onOthersChange]);

  if (!others.length) return null;

  const names = others.map((o) => o.user_name).filter(Boolean);
  return (
    <div data-testid="kb-editing-presence"
      className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <Users className="h-4 w-4 shrink-0 text-amber-600" />
      <span className="font-semibold" data-testid="kb-editing-presence-names">
        {names.length === 1 ? `${names[0]} is editing this right now` : `${names.join(", ")} are editing this right now`}
      </span>
      <span className="text-amber-700">
        (opened {sinceLabel(others[0].started_at)}) — whoever saves last wins, so agree who is making the change.
      </span>
    </div>
  );
}
